import { useState } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useReactTable, getCoreRowModel, flexRender } from '@tanstack/react-table';
import { MoreHorizontal, Eye, EyeOff, Copy, RefreshCw, Trash2, Pencil } from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DataTablePagination } from '@/components/ui/data-table/pagination';
import { toast } from '@/components/ui/sonner';
import { useUpdateTier, useRotateKey, useDeleteToolClient } from '@/hooks';
import type { ToolClient, AccessTier } from '@/services';

// ─── helpers ─────────────────────────────────────────────────────────────────

const TIER_BADGE: Record<AccessTier, { label: string; className: string }> = {
  full_access: { label: 'Full Access', className: 'bg-green-100 text-green-700 border-green-200' },
  read_only:   { label: 'Read Only',   className: 'bg-sky-100 text-sky-700 border-sky-200' },
  write_only:  { label: 'Write Only',  className: 'bg-amber-100 text-amber-700 border-amber-200' },
  dev:         { label: 'Dev',         className: 'bg-violet-100 text-violet-700 border-violet-200' },
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function maskKey(key: string) {
  return key.slice(0, 7) + '••••••••••••••••••••••••' + key.slice(-4);
}

// ─── Key cell ────────────────────────────────────────────────────────────────

function KeyCell({ activeKey }: { activeKey: string | null }) {
  const [visible, setVisible] = useState(false);

  if (!activeKey) return <span className='text-muted-foreground text-sm'>—</span>;

  return (
    <div className='flex items-center gap-1.5 font-mono text-xs'>
      <span className='max-w-[220px] truncate'>{visible ? activeKey : maskKey(activeKey)}</span>
      <Button
        variant='ghost'
        size='icon'
        className='h-6 w-6 shrink-0'
        onClick={() => setVisible((v) => !v)}
        title={visible ? 'Hide key' : 'Show key'}
      >
        {visible ? <EyeOff className='h-3.5 w-3.5' /> : <Eye className='h-3.5 w-3.5' />}
      </Button>
      <Button
        variant='ghost'
        size='icon'
        className='h-6 w-6 shrink-0'
        onClick={() => {
          navigator.clipboard.writeText(activeKey);
          toast.success('Key copied to clipboard');
        }}
        title='Copy key'
      >
        <Copy className='h-3.5 w-3.5' />
      </Button>
    </div>
  );
}

// ─── Edit tier dialog ─────────────────────────────────────────────────────────

function EditTierDialog({ client, onClose }: { client: ToolClient; onClose: () => void }) {
  const [tier, setTier] = useState<AccessTier>(client.accessTier);
  const updateTier = useUpdateTier();

  const handleSave = async () => {
    try {
      await updateTier.mutateAsync({ clientId: client.clientId, accessTier: tier });
      toast.success('Access tier updated');
      onClose();
    } catch {
      toast.error('Failed to update access tier');
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle>Edit Access Tier</DialogTitle>
          <DialogDescription>
            Change the access tier for <span className='font-mono font-semibold'>{client.clientId}</span>.
          </DialogDescription>
        </DialogHeader>
        <Select value={tier} onValueChange={(v) => setTier(v as AccessTier)}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='full_access'>Full Access</SelectItem>
            <SelectItem value='read_only'>Read Only</SelectItem>
            <SelectItem value='write_only'>Write Only</SelectItem>
            <SelectItem value='dev'>Dev (write tools return dummy data)</SelectItem>
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={updateTier.isPending}>
            {updateTier.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Confirm dialog ───────────────────────────────────────────────────────────

function ConfirmDialog({
  title,
  description,
  confirmLabel,
  destructive,
  onConfirm,
  onClose,
  isPending,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  destructive?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className='max-w-sm'>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant='outline' onClick={onClose}>Cancel</Button>
          <Button variant={destructive ? 'destructive' : 'default'} onClick={onConfirm} disabled={isPending}>
            {isPending ? 'Working…' : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Row actions ──────────────────────────────────────────────────────────────

function RowActions({ client }: { client: ToolClient }) {
  const [dialog, setDialog] = useState<'edit-tier' | 'rotate' | 'delete' | null>(null);
  const rotateKey = useRotateKey();
  const deleteClient = useDeleteToolClient();

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant='ghost' className='h-8 w-8 p-0' onClick={(e) => e.stopPropagation()}>
            <span className='sr-only'>Open menu</span>
            <MoreHorizontal className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem onClick={() => setDialog('edit-tier')}>
            <Pencil className='mr-2 h-4 w-4' /> Edit tier
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setDialog('rotate')}>
            <RefreshCw className='mr-2 h-4 w-4' /> Rotate key
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem className='text-destructive focus:text-destructive' onClick={() => setDialog('delete')}>
            <Trash2 className='mr-2 h-4 w-4' /> Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {dialog === 'edit-tier' && (
        <EditTierDialog client={client} onClose={() => setDialog(null)} />
      )}
      {dialog === 'rotate' && (
        <ConfirmDialog
          title='Rotate key?'
          description={`This will generate a new secret for "${client.clientId}". The old key stops working immediately.`}
          confirmLabel='Rotate'
          onConfirm={async () => {
            try {
              await rotateKey.mutateAsync(client.clientId);
              toast.success('Key rotated');
              setDialog(null);
            } catch {
              toast.error('Failed to rotate key');
            }
          }}
          onClose={() => setDialog(null)}
          isPending={rotateKey.isPending}
        />
      )}
      {dialog === 'delete' && (
        <ConfirmDialog
          title='Delete client?'
          description={`"${client.clientId}" will be permanently deleted and can no longer authenticate.`}
          confirmLabel='Delete'
          destructive
          onConfirm={async () => {
            try {
              await deleteClient.mutateAsync(client.clientId);
              toast.success('Client deleted');
              setDialog(null);
            } catch {
              toast.error('Failed to delete client');
            }
          }}
          onClose={() => setDialog(null)}
          isPending={deleteClient.isPending}
        />
      )}
    </>
  );
}

// ─── Column definitions ───────────────────────────────────────────────────────

const columns: ColumnDef<ToolClient>[] = [
  {
    accessorKey: 'clientId',
    header: 'Client ID',
    cell: ({ row }) => (
      <div className='font-mono text-sm font-medium'>{row.original.clientId}</div>
    ),
  },
  {
    accessorKey: 'accessTier',
    header: 'Access Tier',
    cell: ({ row }) => {
      const cfg = TIER_BADGE[row.original.accessTier];
      return (
        <Badge variant='outline' className={cfg.className}>
          {cfg.label}
        </Badge>
      );
    },
  },
  {
    accessorKey: 'activeKey',
    header: 'Active Key',
    cell: ({ row }) => <KeyCell activeKey={row.original.activeKey} />,
  },
  {
    accessorKey: 'activeKeyExpiresAt',
    header: 'Expires',
    cell: ({ row }) =>
      row.original.activeKeyExpiresAt
        ? <div className='text-muted-foreground'>{formatDate(row.original.activeKeyExpiresAt)}</div>
        : <div className='text-muted-foreground'>Never</div>,
  },
  {
    accessorKey: 'createdAt',
    header: 'Created',
    cell: ({ row }) => <div className='text-muted-foreground'>{formatDate(row.original.createdAt)}</div>,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => <RowActions client={row.original} />,
  },
];

// ─── Table ────────────────────────────────────────────────────────────────────

interface ToolClientsTableProps {
  clients: ToolClient[];
  isLoading: boolean;
  page: number;
  limit: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (limit: number) => void;
}

export function ToolClientsTable({
  clients,
  isLoading,
  page,
  limit,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: ToolClientsTableProps) {
  const table = useReactTable({
    data: clients,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    pageCount: totalPages,
    state: { pagination: { pageIndex: page - 1, pageSize: limit } },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex: page - 1, pageSize: limit }) : updater;
      if (next.pageIndex !== page - 1) onPageChange(next.pageIndex + 1);
      if (next.pageSize !== limit) onPageSizeChange(next.pageSize);
    },
  });

  return (
    <div className='space-y-4'>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => (
                <TableHead key={h.id}>
                  {h.isPlaceholder ? null : flexRender(h.column.columnDef.header, h.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-48 text-center'>
                <div className='flex flex-col items-center justify-center gap-2'>
                  <Spinner className='size-6' />
                  <span className='text-sm text-muted-foreground'>Loading clients...</span>
                </div>
              </TableCell>
            </TableRow>
          ) : table.getRowModel().rows.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className='h-24 text-center text-muted-foreground'>
                No clients found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <DataTablePagination table={table} pageSizeOptions={[10, 20, 30, 40, 50]} showRowSelection={false} />
    </div>
  );
}
